/**
 * Human-readable names for every acknowledged-able tooltip.
 *
 * Tooltip identities live in the `data-tipkey` attribute of the tipped
 * elements — on the dashboard (src/components/*), and on the /view pages
 * (functions/_lib/banner.tsx, rendered server-side). This registry is the
 * single place that maps those keys to the labels shown in the Settings
 * panel's "Tooltips" section, so users can see WHICH tips they have
 * acknowledged and reset them individually.
 *
 * Keys missing from this registry (e.g. future tips) fall back to their
 * raw key, so a renamed/added tip never breaks the list.
 */

export type TipMeta = {
  /** Short name of the UI element the tip belongs to. */
  label: string;
  /** Where in the app the element lives. */
  where: string;
};

export const TIP_LABELS: Record<string, TipMeta> = {
  // --- Dashboard: header
  "header-settings": { label: "Settings button", where: "Header" },
  "header-poll": { label: "“Poll now” button", where: "Header" },
  "header-preferred": { label: "Preferred instance chip", where: "Header" },

  // --- Dashboard: instance rows
  "row-score": { label: "Score column", where: "Instance rows" },
  "row-view": { label: "Instance domain link", where: "Instance rows" },
  "row-ext": { label: "Open-instance icon", where: "Instance rows" },
  "row-preferred-flag": { label: "“Preferred” badge", where: "Instance rows" },
  "row-noiframe": { label: "“no iframe” note", where: "Instance rows" },
  "row-ping": { label: "Ping column", where: "Instance rows" },
  "row-samples": { label: "Polls column", where: "Instance rows" },
  "row-lastok": { label: "“Last ok” column", where: "Instance rows" },
  "row-bias": { label: "Priority bar", where: "Instance rows" },
  "act-boost": { label: "Boost button", where: "Instance rows" },
  "act-penalize": { label: "Penalize button", where: "Instance rows" },
  "act-prefer": { label: "Prefer button", where: "Instance rows" },

  // /view banner (server-rendered toolbar + minimized pill)
  "banner:brand": { label: "Nitter brand link", where: "/v toolbar" },
  "banner:menu": { label: "Menu button", where: "/v toolbar" },
  "banner:prefer": { label: "Prefer button", where: "/v toolbar" },
  "banner:who": { label: "Current instance label", where: "/v toolbar" },
  "banner:who-link": { label: "Current instance link", where: "/v toolbar" },
  "banner:redirect": { label: "“Always redirect to X.com” toggle", where: "/v toolbar" },
  "banner:boost": { label: "Boost button", where: "/v toolbar" },
  "banner:bias": { label: "Priority readout", where: "/v toolbar" },
  "banner:penalize": { label: "Penalize button", where: "/v toolbar" },
  "banner:skipped": { label: "“Skipped” counter", where: "/v toolbar" },
  "banner:notice": { label: "Proxy-mode notice", where: "/v toolbar" },
  "banner:embed-hint": { label: "Embed caveat", where: "/v toolbar" },
  "banner:dock": { label: "Dock top/bottom button", where: "/v toolbar" },
  "banner:minimize": { label: "Minimize button", where: "/v toolbar" },
  "banner:fail": { label: "“Instance broken” button", where: "/v toolbar" },
  "banner:donate": { label: "Donate chip", where: "/v toolbar" },
  "banner:donate-close": { label: "Donate dismiss button", where: "/v toolbar" },
  "banner:retry": { label: "Retry button", where: "/v toolbar" },
  "banner:pill": { label: "Minimized pill", where: "/v pill" },
  "banner:pill-domain": { label: "Pill instance link", where: "/v pill" },
  "banner:pill-retry": { label: "Pill retry button", where: "/v pill" },
  "banner:pill-dock": { label: "Pill dock button", where: "/v pill" },
};

/** Metadata for a tip key — falls back to the raw key when unknown. */
export function tipMeta(key: string): TipMeta {
  return TIP_LABELS[key] ?? { label: key, where: "Unknown" };
}
